import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Grid,
  Snackbar,
  Switch,
  Typography,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { withTranslation } from "react-i18next";
import { useUserStore } from "store";
import { getCsrfToken } from "../../utils";
import InboxAddressCard from "../supplier-orders/inbound/InboxAddressCard";

// "Sähköiset lähetteet" sub-tab of the Ominaisuudet (Features) settings tab.
// Available to every org. Holds the org's secret lähete inbox address and the
// on/off toggle (on by default; the toggle is a support off-switch).
function FeaturesInboundDeliveriesTab({ t, onDirtyChange }) {
  const { currentOrganization, fetchUser } = useUserStore();
  const [form, setForm] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  useEffect(() => {
    if (!currentOrganization) return;
    setForm({
      inbound_delivery_notes_enabled: currentOrganization.inbound_delivery_notes_enabled || false,
      inbound_delivery_notes_auto_resolve: currentOrganization.inbound_delivery_notes_auto_resolve || false,
    });
    setIsDirty(false);
  }, [currentOrganization?.organization_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggle = (event) => {
    const checked = event.target.checked;
    setForm((prev) => ({ ...prev, inbound_delivery_notes_enabled: checked }));
    setIsDirty(true);
  };

  const handleAutoResolveToggle = (event) => {
    const checked = event.target.checked;
    setForm((prev) => ({ ...prev, inbound_delivery_notes_auto_resolve: checked }));
    setIsDirty(true);
  };

  const handleSave = async () => {
    if (!form) return;
    try {
      setSaving(true);
      await axios.patch(
        `/api/organizations/${currentOrganization.organization_id}/`,
        {
          inbound_delivery_notes_enabled: form.inbound_delivery_notes_enabled,
          inbound_delivery_notes_auto_resolve: form.inbound_delivery_notes_auto_resolve,
        },
        {
          headers: {
            "X-CSRFToken": getCsrfToken(),
            "X-Organization-Id": currentOrganization.organization_id,
          },
        }
      );
      // Refresh user store so the supplier-orders tab and nav gating update immediately
      await fetchUser();
      setIsDirty(false);
      setSnackbar({
        open: true,
        message: t("Organization updated successfully"),
        severity: "success",
      });
    } catch (error) {
      console.error("Failed to update inbound delivery note settings:", error);
      const message = error.response?.data?.detail || t("Failed to update organization");
      setSnackbar({ open: true, message, severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  // Report dirty state to parent (sub-tab + tab-switch guards).
  const handleSaveRef = useRef(handleSave);
  handleSaveRef.current = handleSave;

  useEffect(() => {
    onDirtyChange?.(isDirty, () => handleSaveRef.current());
  }, [isDirty, onDirtyChange]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!form) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const enabled = form.inbound_delivery_notes_enabled;
  const autoResolve = form.inbound_delivery_notes_auto_resolve;
  const inventoryTracking = !!currentOrganization?.inventory_tracking_enabled;

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={enabled}
                onChange={handleToggle}
                data-testid="inbound-deliveries-toggle"
              />
            }
            label={t("inboundDeliveries.featureToggle")}
          />
          <Typography variant="body2" color="text.secondary" sx={{ ml: 6 }}>
            {t("inboundDeliveries.featureToggleHelp")}
          </Typography>
        </Grid>

        {enabled && (
          <Grid item xs={12}>
            <Typography
              variant="overline"
              sx={{ fontWeight: 700, color: "text.secondary", letterSpacing: ".08em" }}
            >
              {t("inboundDeliveries.inboxAddressLabel")}
            </Typography>
            <Box sx={{ maxWidth: 560, mt: 0.5 }}>
              <InboxAddressCard />
            </Box>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mt: 1.5, maxWidth: 560 }}>
              <LockOutlinedIcon sx={{ fontSize: 16, color: "text.disabled", mt: "2px" }} />
              <Typography variant="caption" color="text.secondary">
                {t("inboundDeliveries.addressSecrecyNote")}
              </Typography>
            </Box>
          </Grid>
        )}

        {enabled && (
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={autoResolve}
                  onChange={handleAutoResolveToggle}
                  data-testid="inbound-auto-resolve-toggle"
                />
              }
              label={t("inboundDeliveries.autoResolveToggle")}
            />
            <Typography variant="body2" color="text.secondary" sx={{ ml: 6, maxWidth: 560 }}>
              {t("inboundDeliveries.autoResolveToggleHelp")}
            </Typography>
            {autoResolve && !inventoryTracking && (
              <Alert severity="info" sx={{ ml: 6, mt: 1, maxWidth: 560 }} data-testid="inbound-auto-resolve-tracking-note">
                {t("inboundDeliveries.autoResolveTrackingOffNote")}
              </Alert>
            )}
          </Grid>
        )}

        {!enabled && isDirty === false && (
          <Grid item xs={12}>
            <Alert severity="info" sx={{ maxWidth: 560 }}>
              {t("inboundDeliveries.featureOffInfo")}
            </Alert>
          </Grid>
        )}

        <Grid item xs={12}>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={saving || !isDirty}
              data-testid="inbound-deliveries-settings-save"
            >
              {saving ? <CircularProgress size={20} /> : <span>{t("Save Changes")}</span>}
            </Button>
          </Box>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default withTranslation()(FeaturesInboundDeliveriesTab);
